import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ChevronRight } from "lucide-react";

/**
 * Landing sequence — deliberate, unhurried.
 *
 *   Stage 1  connecting        —  molten plasma is already drifting under the
 *                                  obsidian surface; a monospaced line reads
 *                                  "connecting, please wait . . ." and lands on
 *                                  "established." with a soft blinking cursor.
 *                                  Duration: 3.4s
 *   Stage 2  glyph              —  the terminal line fades, an oversized (200px)
 *                                  cobalt tipi glyph pulses into view over the
 *                                  plasma. Duration: 2.6s
 *   Stage 3  greeting           —  the glyph settles smaller, and "Kasabonika
 *                                  Lake First Nation" slowly emerges beneath it
 *                                  (letter-spacing tightens, blur clears).
 *                                  Duration: 3.8s
 *   Stage 4  hero               —  gentle cross-fade to the real page.
 *
 * Total: ~9.8s. Users who have seen it this session skip to stage 4.
 * ESC skips at any point.
 */

const SKIP_KEY = "klfn_boot_seen_v3";

function TipiGlyph({ size = 96, className = "", strokeWidth = 1.4 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none"
      stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="square" strokeLinejoin="miter"
      className={className} aria-label="Kasabonika Lake tipi glyph">
      <line x1="14" y1="4" x2="10.5" y2="8" />
      <line x1="18" y1="4" x2="21.5" y2="8" />
      <line x1="16" y1="3" x2="16" y2="7" />
      <line x1="16" y1="6" x2="4" y2="27" />
      <line x1="16" y1="6" x2="28" y2="27" />
      <line x1="4" y1="27" x2="28" y2="27" />
      <polyline points="13,27 16,20 19,27" />
    </svg>
  );
}

export default function Landing() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [stage, setStage] = useState(() => (sessionStorage.getItem(SKIP_KEY) ? "hero" : "connecting"));

  useEffect(() => {
    if (stage === "hero") return;
    const timers = [];
    // Stage 1 → 2 at 3.4s
    timers.push(setTimeout(() => setStage("glyph"), 3400));
    // Stage 2 → 3 at 6.0s
    timers.push(setTimeout(() => setStage("greeting"), 6000));
    // Stage 3 → 4 at 9.8s
    timers.push(setTimeout(() => {
      setStage("hero");
      sessionStorage.setItem(SKIP_KEY, "1");
    }, 9800));
    const onKey = (e) => {
      if (e.key === "Escape") {
        timers.forEach(clearTimeout);
        setStage("hero");
        sessionStorage.setItem(SKIP_KEY, "1");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => { timers.forEach(clearTimeout); window.removeEventListener("keydown", onKey); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (stage !== "hero") return <BootSequence stage={stage} />;
  return <Hero user={user} nav={nav} />;
}

/* ==================================================
 * HERO — leaner, no redundant Council block
 * ================================================== */
function Hero({ user, nav }) {
  return (
    <div className="fade-in relative">
      {/* Section 1 — introduction */}
      <section className="watermark-container relative border-b border-white/5 hex-grid overflow-hidden">
        <div className="plasma-bg" aria-hidden />
        <div className="plasma-veil" aria-hidden />
        <TipiGlyph size={780} className="watermark-tipi" />
        <div className="relative max-w-[1240px] mx-auto px-6 py-28 lg:py-40">
          <div className="max-w-3xl">
            <div className="label-mono mb-6 flex items-center gap-3" style={{ color: "var(--mauve)" }}>
              <span className="w-6 h-px" style={{ background: "var(--mauve)" }} />
              Treaty 9 · Nishnawbe-Aski Nation · Ontario
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight" style={{ color: "var(--text)" }}>
              KASABONIKA LAKE
              <br/>
              <span style={{ color: "var(--mauve)" }}>FIRST NATION</span>
            </h1>
            <p className="mt-9 max-w-xl text-lg leading-relaxed" style={{ color: "var(--text-dim)" }}>
              The official governance portal — verified band-member access, sanctioned community
              consultation, and every action of Chief &amp; Council on the record.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              {user && user.status === "approved" ? (
                <button className="btn-cta primary" onClick={() => nav("/portal")} data-testid="hero-enter-portal">
                  Enter portal <ChevronRight size={14} />
                </button>
              ) : (
                <>
                  <button className="btn-cta primary" onClick={() => nav("/register")} data-testid="hero-register-btn">
                    Request access <ChevronRight size={14} />
                  </button>
                  <button className="btn-cta" onClick={() => nav("/login")} data-testid="hero-login-btn">
                    Sign in
                  </button>
                </>
              )}
              <button className="btn-cta ghost" onClick={() => nav("/careers")} data-testid="hero-careers-btn">
                See open positions
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Section 2 — value pillars, tightened */}
      <section className="border-b border-white/5">
        <div className="max-w-[1240px] mx-auto px-6 py-24">
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <div className="label-mono">What lives here</div>
              <h2 className="font-display text-3xl mt-3 leading-tight" style={{ color: "var(--text)" }}>
                Nation business, in one place.
              </h2>
              <p className="mt-4 text-sm leading-relaxed" style={{ color: "var(--text-dim)" }}>
                Six functions, one signed record. Everything below is live to elected leadership and
                accountable to the membership.
              </p>
            </div>
            <div className="lg:col-span-8 grid sm:grid-cols-2 gap-3">
              {[
                { n: "01", title: "Verified registry",     body: "Every account is reviewed by the band registry clerk against legacy records." },
                { n: "02", title: "Sanctioned consultation", body: "Council polls, surveys and ballots — single vote, immutable trail." },
                { n: "03", title: "Command & operations", body: "Chief, Council and Executive run dockets, dependencies and budgets live." },
                { n: "04", title: "Automated reporting",   body: "ISC, funding-program and internal reports assembled from live data." },
                { n: "05", title: "Human resources",       body: "Digital employee folders, postings, and onboarding on the record." },
                { n: "06", title: "Public transparency",   body: "Ledger, careers and departments open to the community." },
              ].map((p) => (
                <div key={p.n} className="panel p-5">
                  <div className="font-mono text-[10px] tracking-widest" style={{ color: "var(--text-mute)" }}>{p.n}</div>
                  <div className="font-display text-lg mt-2" style={{ color: "var(--text)" }}>{p.title}</div>
                  <div className="text-sm mt-2 leading-relaxed" style={{ color: "var(--text-dim)" }}>{p.body}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Section 3 — one integrated block: Council roster + shortcuts */}
      <section>
        <div className="max-w-[1240px] mx-auto px-6 py-20 grid lg:grid-cols-[1.2fr_1fr] gap-16">
          <div className="panel p-8">
            <div className="label-mono mb-4">Roster · term 2026 – 2028</div>
            <h2 className="font-display text-3xl leading-tight" style={{ color: "var(--text)" }}>
              Elected leadership.
              <br/>
              <span style={{ color: "var(--text-dim)" }}>Publicly accountable.</span>
            </h2>
            <ul className="mt-8 divide-y divide-white/8 text-sm">
              {[
                ["Chief", "Matthias Anderson"],
                ["Deputy Chief", "Terry Stoney"],
                ["Head Councillor", "Rachel Mamakwa"],
                ["Councillor", "Mike Morris"],
                ["Councillor", "Josie Anderson Sr"],
                ["Councillor", "Simon Semple"],
                ["Councillor", "Chantel Stoney"],
                ["Councillor", "Cassidy Anderson"],
              ].map(([role, name], i) => (
                <li key={name} className="flex items-center gap-4 py-3">
                  <span className="w-6 font-mono text-[10px] tabular-nums" style={{ color: "var(--text-mute)" }}>
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="flex-1" style={{ color: "var(--text)" }}>{name}</span>
                  <span className="font-mono text-[10px] tracking-widest whitespace-nowrap uppercase" style={{ color: "var(--blue)" }}>
                    {role}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <div className="label-mono">Community entry points</div>
            <h3 className="font-display text-2xl mt-2 leading-tight" style={{ color: "var(--text)" }}>
              Start where you need to.
            </h3>
            <div className="mt-6 space-y-3">
              {[
                { to: "/careers",      label: "Careers",              caption: "Open positions and applications" },
                { to: "/transparency", label: "Transparency ledger",   caption: "Every governance action, on record" },
                { to: "/departments",  label: "Departments",           caption: "Program areas and staff directory" },
                { to: "/about",        label: "About the Nation",      caption: "History, treaty, community profile" },
                { to: "/contact",      label: "Contact",               caption: "Band office and department contacts" },
              ].map(l => (
                <button key={l.to} onClick={() => nav(l.to)}
                  className="w-full panel panel-lift p-4 text-left flex items-center gap-4 group"
                  data-testid={`hero-shortcut-${l.to.slice(1)}`}>
                  <div className="flex-1">
                    <div className="font-display text-base" style={{ color: "var(--text)" }}>{l.label}</div>
                    <div className="font-mono text-[10px] uppercase tracking-widest mt-1" style={{ color: "var(--text-mute)" }}>
                      {l.caption}
                    </div>
                  </div>
                  <ChevronRight size={16} className="transition-transform group-hover:translate-x-1" style={{ color: "var(--mauve)" }}/>
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

/* ==================================================
 * BOOT SEQUENCE
 * ================================================== */
function BootSequence({ stage }) {
  return (
    <div className="fixed inset-0 z-50 overflow-hidden" style={{ background: "var(--bg)" }}>
      {/* Molten plasma undertone, always on */}
      <div className="plasma-bg" aria-hidden />
      <div className="plasma-veil" aria-hidden />

      {/* Stage 1 — connecting */}
      <div
        data-testid="boot-connecting"
        className={`absolute inset-0 flex items-center justify-center transition-opacity duration-[1100ms] ease-out ${
          stage === "connecting" ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      >
        <div className="font-mono text-sm sm:text-base tracking-wide" style={{ color: "var(--text-dim)" }}>
          {stage === "connecting"
            ? <span className="cursor-soft">connecting, please wait . . . <span style={{ color: "var(--blue)" }}>established.</span></span>
            : <span>connecting, please wait . . . <span style={{ color: "var(--blue)" }}>established.</span></span>
          }
        </div>
      </div>

      {/* Stage 2 — glyph */}
      <div
        data-testid="boot-glyph"
        className={`absolute inset-0 flex items-center justify-center transition-opacity duration-[1100ms] ease-out ${
          stage === "glyph" ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      >
        <div className="glyph-pulse" style={{ color: "var(--blue)" }}>
          <TipiGlyph size={200} strokeWidth={1.15} />
        </div>
      </div>

      {/* Stage 3 — greeting + slow name reveal */}
      <div
        data-testid="boot-greeting"
        className={`absolute inset-0 flex items-center justify-center px-6 transition-opacity duration-[1100ms] ease-out ${
          stage === "greeting" ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      >
        <div className="text-center">
          <div className="flex justify-center mb-10 glow-in" style={{ color: "var(--blue)" }}>
            <TipiGlyph size={96} strokeWidth={1.05} />
          </div>
          <div className="font-mono text-sm tracking-[0.4em]" style={{ color: "var(--text-mute)" }}>
            HELLO
          </div>
          <div className="mt-6 font-display text-2xl sm:text-3xl lg:text-4xl name-emerge" style={{ color: "var(--text)" }}>
            KASABONIKA LAKE FIRST NATION
          </div>
          <div className="font-mono text-[10px] tracking-[0.32em] mt-6" style={{ color: "var(--text-mute)" }}>
            EST. TREATY 9 · 1929
          </div>
        </div>
      </div>

      <div className="absolute bottom-8 right-8 font-mono text-[10px] tracking-widest uppercase" style={{ color: "var(--text-mute)" }}>
        press esc to skip
      </div>
    </div>
  );
}
